import React from "react";
import "./RarityStats.css";

function parseRange(value) {
  if (!value) return null;

  const cleaned = String(value).replace(/,/g, "").trim();
  const parts = cleaned.split("-").map((p) => Number(p.trim()));

  if (parts.length !== 2 || parts.some(Number.isNaN)) {
    return null;
  }

  return {
    min: parts[0],
    max: parts[1],
  };
}

function getRarityLabel(score) {
  switch (score) {
    case 1:
      return "Common";
    case 2:
      return "Uncommon";
    case 3:
      return "Scarce";
    case 4:
      return "Rare";
    case 5:
      return "Very rare";
    default:
      return null;
  }
}

function formatNumber(num) {
  return new Intl.NumberFormat("en-GB").format(num);
}

function formatRange(range) {
  if (!range) return "Unknown";
  if (range.min === range.max) return formatNumber(range.min);
  return `${formatNumber(range.min)}–${formatNumber(range.max)}`;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getMidpoint(range) {
  return range ? (range.min + range.max) / 2 : null;
}

function getSurvivalRate(productionRange, survivorRange) {
  if (!productionRange || !survivorRange) return null;

  const minRate = (survivorRange.min / productionRange.max) * 100;
  const maxRate = (survivorRange.max / productionRange.min) * 100;
  const avgRate =
    (getMidpoint(survivorRange) / getMidpoint(productionRange)) * 100;

  return {
    min: clamp(minRate, 0, 100),
    max: clamp(maxRate, 0, 100),
    avg: clamp(avgRate, 0, 100),
  };
}

/*
  Heuristic rarity score:
  - fewer survivors = rarer
  - fewer built = rarer
  - lower survival rate = rarer
*/
function getRarityScore(productionRange, survivorRange, survivalRate) {
  if (!productionRange || !survivorRange || !survivalRate) return null;

  const built = getMidpoint(productionRange);
  const surviving = getMidpoint(survivorRange);
  const rate = survivalRate.avg;

  let score = 1;

  if (surviving <= 50) score += 2;
  else if (surviving <= 250) score += 1.5;
  else if (surviving <= 1000) score += 1;

  if (built <= 250) score += 1.5;
  else if (built <= 1000) score += 1;
  else if (built <= 5000) score += 0.5;

  if (rate <= 5) score += 1;
  else if (rate <= 10) score += 0.5;

  return clamp(Math.round(score), 1, 5);
}

function WheelIcon({ filled }) {
  return (
    <svg
      className={`rarity-stats-wheel ${filled ? "is-filled" : ""}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="rarity-stats-wheel-outer" cx="12" cy="12" r="9" />
      <circle className="rarity-stats-wheel-inner" cx="12" cy="12" r="3.2" />
      <path className="rarity-stats-wheel-spoke" d="M12 3v6" />
      <path className="rarity-stats-wheel-spoke" d="M12 15v6" />
      <path className="rarity-stats-wheel-spoke" d="M3 12h6" />
      <path className="rarity-stats-wheel-spoke" d="M15 12h6" />
      <path className="rarity-stats-wheel-spoke" d="M5.6 5.6l4.2 4.2" />
      <path className="rarity-stats-wheel-spoke" d="M14.2 14.2l4.2 4.2" />
      <path className="rarity-stats-wheel-spoke" d="M18.4 5.6l-4.2 4.2" />
      <path className="rarity-stats-wheel-spoke" d="M9.8 14.2l-4.2 4.2" />
    </svg>
  );
}

export default function RarityStats({
  estimatedProduction,
  estimatedSurvivors,
}) {
  const productionRange = parseRange(estimatedProduction);
  const survivorRange = parseRange(estimatedSurvivors);
  const survivalRate = getSurvivalRate(productionRange, survivorRange);
  const rarityScore = getRarityScore(
    productionRange,
    survivorRange,
    survivalRate
  );

  if (!productionRange && !survivorRange) {
    return null;
  }

  return (
    <section className="rarity-stats-card" aria-label="Rarity statistics">
      <div className="rarity-stats-header">
        <h4 className="rarity-stats-title">Rarity</h4>

        {rarityScore && (
          <div
            className="rarity-stats-rating"
            aria-label={`Rarity ${rarityScore} out of 5`}
            title={`Rarity ${rarityScore}/5`}
          >
           <span className="rarity-stats-rating-text">
  {getRarityLabel(rarityScore)}
</span>
            <div className="rarity-stats-rating-icons">
              {[1, 2, 3, 4, 5].map((n) => (
                <WheelIcon key={n} filled={n <= rarityScore} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rarity-stats-grid">
        <div className="rarity-stats-item">
          <span className="rarity-stats-label">Built</span>
          <span className="rarity-stats-value">
            {formatRange(productionRange)}
          </span>
        </div>

        <div className="rarity-stats-item">
          <span className="rarity-stats-label">Surviving</span>
          <span className="rarity-stats-value">
            {formatRange(survivorRange)}
          </span>
        </div>

        {survivalRate && (
          <div className="rarity-stats-item rarity-stats-item-wide">
            <div className="rarity-stats-rate-row">
              <span className="rarity-stats-label">Survival rate</span>
              <span className="rarity-stats-value">
                {survivalRate.min.toFixed(1)}–{survivalRate.max.toFixed(1)}%
              </span>
            </div>

            <div
              className="rarity-stats-bar"
              aria-label={`Average survival rate ${survivalRate.avg.toFixed(1)} percent`}
            >
              <div
                className="rarity-stats-bar-fill"
                style={{ width: `${survivalRate.avg}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}